import type { PhonemeKind } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/pianoRoll/phonemes/types";
import type {
  AiBackendRun,
  BackingTrackBpmSuggestionSource,
  HistoryChangeKind,
} from "@/app/(application)/(ttv)/ttv/types";
import type { ModelSortVariant } from "@/types/defs/modelSortVariant";

export const TTV_ANALYTICS_EVENT_SCHEMA_VERSION = 1;

// A dragged phoneme as analytics reports it: the lower-cased inventory token and its class.
export type TtvDraggedPhoneme = { code: string; kind: PhonemeKind };

// The user's persisted TTV history as the page read it, BEFORE this visit stamped itself onto it. Serialized rather than a Date because it crosses the server/client boundary like every other timestamp the editor is handed. Both fields answer questions about the USER, which is why neither can be derived from the project list: a user who deletes everything still has a history, and inferring it from what survives reported them as a first-ever visitor.
export type TtvSessionLifecycleSnapshot = {
  // When the previous visit began, or null when there has never been one. Never the newest project's last_opened_at: that moves whenever a project is picked out of the library, and it dies with the project.
  previousSessionAtString: string | null;
  // Whether this user has ever had real TTV work — the persisted sticky flag, ORed with the live evidence of a surviving part so a pre-existing user is answered correctly on the very first visit after the row exists.
  hasEverHadTtvWork: boolean;
};

// Where a project was started from. Threaded as an argument rather than inferred, because every creation path funnels through the same two persistence functions and the entry point is the only thing that distinguishes them.
export type TtvProjectCreationSource =
  | "project_menu"
  | "library_modal"
  | "template"
  | "welcome"
  | "vocal_import"
  // A copy made from an existing project. It creates a row and lands the editor in it, exactly like the other three, and used to reach PostHog as neither a creation nor a reopen.
  | "duplicate"
  // The default the server mints when the user deletes every project they have. The editor lands in a row that is seconds old, which is a creation — reporting it as a reopen put a ~0 "days since last opened" on a project nobody had ever opened.
  | "default_replacement";

// How the editor came to be showing a project it was not showing before. The explicit library switch is a decision; the delete promotion is the editor landing somewhere because the project it was in stopped existing.
export type TtvProjectReopenSource = "library" | "delete_promotion";

// What the voice picker was opened to decide — the existing VoiceModalFlow discriminant, plus the seeded variant the + card offers.
export type TtvSingerPickerSource =
  | "add_singer"
  | "copy_melody"
  | "change_voice";

// How the user reached the voice they picked. There is no "featured" tier: VOICES is a flat static catalog, so a voice is found by typing, by narrowing the gender filter, or by scrolling the full grid.
export type TtvSingerDiscoveryMethod = "search" | "filter" | "scroll";

// How the picker's list looked when a voice was picked. Narrowing (discoveryMethod) and ordering (sortVariant) are separate axes: sorting combines with filtering and search, so one enum could not name both. listPosition indexes the list as narrowed AND ordered, so it only means something next to the other two.
export type TtvSingerDiscovery = {
  discoveryMethod: TtvSingerDiscoveryMethod;
  sortVariant: ModelSortVariant;
  listPosition: number;
};

// The ways notes actually enter the score today. Deliberately not the originally proposed drawn/pasted/imported: TTV has no MIDI or file import, and a lyric with more syllables than notes appends its own.
export type TtvNoteInputMethod =
  | "click"
  | "paint"
  | "pasted"
  | "lyric_syllable"
  | "harmony_stack"
  // "Start from X's melody" on the + card: a new singer seeded with a clone of another singer's whole part. The same write addHarmonyStack reports, reached from the roster instead of the preset gallery.
  | "copied_part";

// What the USER did to ask for audio. The generation funnel counts these, not the backend runs each one fans out into: one tempo change on a six-singer roster is one auto_edit intent, not six generations, and a Choir click is one harmony_preset intent rather than one per layer. Per-run detail stays in the stability trail.
export type TtvGenerationIntentSource =
  | "resing"
  | "full_roster_resing"
  | "harmony_preset"
  | "auto_edit"
  // An undo, a redo or a version-timeline jump whose restored score the stored recipes cannot reproduce, so the editor has to sing it again. The user asked for a state they already had back, not for a new take, and the cost/abandonment profile of that is nothing like an edit's — which is why it is its own source rather than another auto_edit.
  | "history_restore"
  // "Try again" on the buffering notice: the user asking, once, for whatever the playhead is waiting on. It fans out into one run per missing phrase, which is the same shape as every other gesture here and the same reason it needs an intent of its own.
  | "playback_retry";

// How a generation intent ended. Exactly one of the first three follows every ttv_generate_requested; "partial" is reachable only on the unison stack, where some takes can land while others do not.
export type TtvGenerationOutcome =
  | "succeeded"
  | "failed"
  | "cancelled"
  | "partial";

// What a manual Re-Sing click asked for. Null for every run that was not an explicit Re-Sing.
export type TtvResingScope =
  | "whole_song"
  | "selected_phrase"
  | "selected_notes";

// The controls whose committed changes are worth a funnel. A closed union so a typo cannot silently mint a new control name in PostHog, and so adding a control is a deliberate decision here rather than a string at a call site.
export type TtvControlName =
  | "vibrato"
  | "de_vibrato"
  | "vibrato_rate"
  | "singer_volume"
  | "singer_volume_wheel"
  | "unison_spread"
  | "unison_count"
  // The mix's two mute squares. Their history entries name themselves (singerMuted / backingTrack) and map to no note edit, so without these the only way a user silences a channel reached analytics as nothing at all.
  | "singer_mute"
  | "backing_track_mute"
  | "backing_track_volume"
  | "vocal_master_volume"
  | "bpm"
  | "time_signature"
  | "metronome"
  | "snap_to_grid"
  // The pinned song key, reported as the user's pick ("auto" until they pin one) — the input every harmony preset is built in.
  | "song_key"
  // The synthesis tuning popover's three number inputs.
  | "variance_model"
  | "variance_steps"
  | "acoustic_steps"
  // The Effects section (merged from feat/text-to-vocals). Six continuous dials, five mode selectors and the master switch.
  | "brightness"
  | "compression"
  | "de_esser"
  | "saturation"
  | "delay"
  | "reverb"
  | "compression_mode"
  | "delay_note_value"
  | "delay_style"
  | "reverb_mode"
  | "saturation_mode"
  | "effects_enabled"
  // The Expression card's two per-note knobs, bipolar -100..100 around the AI's own performance. Same family as the vibrato knobs: a note field a knob drags, reported as a control rather than as a piano-roll note edit.
  | "note_breathiness"
  | "note_tension";

// Analytics never carries raw content, so a control value is only ever a number, a short enum-ish label, or a flag.
export type TtvControlValue = number | string | boolean | null;

// Which of the three shapes a control's before/after values are. PostHog assigns a property ONE type at first ingestion, so tempo numbers, "4/4" strings and metronome booleans sharing one property name meant whichever landed first decided whether the other two were filterable at all. The wire carries a separate pair per shape and this says which pair to read.
export type TtvControlValueType = "number" | "text" | "flag";

// The note-level edits the history layer can name. A subset of HistoryChangeKind: roster, render and setting changes are reported by their own events instead.
export type TtvNoteEditType =
  | "lyrics"
  | "note_move"
  | "note_resize"
  | "note_split"
  | "notes_added"
  | "notes_deleted"
  | "notes_replaced"
  | "pronunciation"
  | "phoneme_volume"
  | "phoneme_timing"
  | "pitch_draw";

// What actually failed. Derived from the thrown value at the dispatcher's catch, never the message (which can carry a file name or a server string).
export type TtvGenerationErrorType =
  | "network"
  | "backend_rejected"
  | "no_audio"
  | "decode"
  | "unknown";

// The settings snapshot a generation was requested with. Only parameters TTV actually has: there is no effects rack and no pitch slider, so pitch shaping is reported as the vibrato family plus whether the line was hand-drawn.
export type TtvGenerationSnapshot = {
  // Named for what it is rather than the bare "model" it shipped as: a numeric TTV variance model id and the conversion funnel's string model name must not share one PostHog property, which is typed once per project at ingestion.
  varianceModelId: number | null;
  varianceSteps: number;
  acousticSteps: number;
  // The roster's voices, in slot order. Named for its scope and its contents: it sits beside runSingerCount on the same event, and it holds voice ids rather than roster members.
  rosterVoiceIds: string[];
  // The whole roster. The per-run scope is runSingerCount on the event itself, so the two counts can no longer disagree under one name.
  rosterSingerCount: number;
  unisonLayers: number;
  backingTrackPresent: boolean;
  // The whole score, every singer — distinct from runNoteCount below, which is the part this run actually re-sings.
  projectNoteCount: number;
  // The run singer's own notes. The mean_* family is scoped the same way: this event describes one singer's run, and a roster-wide average dilutes exactly the shaping the user dialled into the part being sung.
  runNoteCount: number;
  // Both describe THIS visit to THIS project, which is what the analytics session can honestly answer — the names say so rather than implying the document carries the fact. Making them document-scoped would need a stored flag; see the PR notes.
  hasPitchEditsThisSession: boolean;
  notesManuallyEditedThisSession: boolean;
  meanVibrato: number;
  meanDeVibrato: number;
  meanVibratoRate: number;
  harmonyPreset: string | null;
  effectsEnabled: boolean;
  // The applied effects preset, or null for "Custom". Read straight off EffectsSettings.presetId, which the store itself clears to null on any manual knob or mode change — so null is an honest "no preset describes this sound" rather than an inference.
  effectsPreset: string | null;
  effectsBrightness: number;
  effectsCompression: number;
  effectsDeEsser: number;
  effectsSaturation: number;
  effectsDelay: number;
  effectsReverb: number;
  effectsCompressionMode: string;
  effectsDelayNoteValue: string;
  effectsDelayStyle: string;
  effectsReverbMode: string;
  effectsSaturationMode: string;
};

export type TtvConversionStage = "validate" | "save" | "render" | "upload";

export type TtvTransferredTake = {
  soundId: string;
  takeIndex: number;
};

type TtvConversionContext = {
  projectId: string;
  singerId: string;
  transferId: string;
};

type TtvConversionEvent =
  | { event: "ttv_convert_shown"; projectId: string }
  | { event: "ttv_convert_clicked"; projectId: string }
  | ({
      event: "ttv_convert_requested";
      takeCount: number;
    } & TtvConversionContext)
  | ({
      event: "ttv_convert_failed";
      stage: TtvConversionStage;
    } & TtvConversionContext)
  | ({
      event: "ttv_convert_transferred";
      takes: TtvTransferredTake[];
      vocalDurationSeconds: number;
      transferMs: number;
      saveMs: number;
      renderMs: number;
      // Wall time from the first registration to all PUTs finishing, overlapping rendering. These phase measurements must not be added together.
      uploadMs: number;
    } & TtvConversionContext)
  | {
      event: "ttv_convert_input_ready";
      soundId: string;
      transferId: string | null;
    }
  | {
      event: "conversion_completed";
      soundId: string;
      conversionId: string;
      transferId: string | null;
    };

export type TtvAnalyticsEvent =
  | TtvConversionEvent
  | {
      event: "ttv_session_started";
      daysSinceLastSession: number | null;
      projectCount: number;
      // Whether this user has done real TTV work before — ANY project of theirs holding notes, not just the one they happen to land in. Reading only the newest project reported a twelve-project veteran as a new user whenever their newest project was empty.
      hasPreviousTtvWork: boolean;
    }
  | {
      event: "ttv_project_created";
      // Named for the decision it describes. A bare `source` on both project events put two unrelated enums on one PostHog key, so a breakdown mixed "where a project was made" with "how the editor landed in one".
      creationSource: TtvProjectCreationSource;
      // OPTIONAL, and absent today. It cannot be measured honestly (see resolveTtvFirstProjectFlag), and an unmeasurable property is omitted from the payload rather than sent as null — a null would still create the property in PostHog and invite a breakdown on a value that means "we never knew". `projectCountBefore` carries what IS measurable. Optional rather than deleted so the day the schema can answer, filling it in is a one-line change with no shape migration.
      isFirstTtvProject?: boolean;
      projectCountBefore: number;
    }
  | {
      event: "ttv_project_reopened";
      reopenSource: TtvProjectReopenSource;
      projectAgeDays: number | null;
      daysSinceLastOpened: number | null;
      projectNoteCount: number;
      // The roster, under the one name every event uses for it. "singer_count" and "active_singer_count" both meant this, and "active" reads as the singer being edited — which is a different thing entirely.
      rosterSingerCount: number;
      hasBackingTrack: boolean;
      bpm: number;
    }
  | {
      event: "ttv_singer_picker_opened";
      openedFrom: TtvSingerPickerSource;
      rosterSingerCount: number;
    }
  | {
      event: "ttv_singer_selected";
      // The VOICE picked, not a roster member. Harmony layers deliberately share the lead's voice, so two slots can carry the same id — the slot below is the discriminator, and the key says which of the two it is.
      voiceId: string;
      replacedVoiceId: string | null;
      // Which roster slot was replaced (the singer's stable colorIndex), or null when the pick opens a new slot. A voice id alone cannot identify a roster member: harmony layers deliberately share the lead's voice, so two slots legitimately carry the same id.
      replacedSingerSlot: number | null;
      discoveryMethod: TtvSingerDiscoveryMethod;
      // The order the list was in. Without it a position under discovery_method "scroll" would mix the roster order with the Recent and Popular orders.
      sortVariant: ModelSortVariant;
      // Position within the list the user was actually looking at — which is the filtered or searched list when either is active, in the chosen sort order, not the catalog index. Named for that scope so a breakdown cannot silently mix the three.
      visibleListPosition: number;
      wasDefaultSingerReplaced: boolean;
    }
  | {
      event: "ttv_singer_added";
      voiceId: string;
      singerSlot: number;
      rosterSingerCount: number;
      wasPartCopied: boolean;
    }
  | {
      event: "ttv_singer_removed";
      voiceId: string;
      singerSlot: number;
      rosterSingerCountAfter: number;
    }
  | {
      event: "ttv_singer_focus_switched";
      previousVoiceId: string;
      previousSingerSlot: number;
      newVoiceId: string;
      newSingerSlot: number;
      rosterSingerCount: number;
    }
  | {
      event: "ttv_notes_entered";
      inputMethod: TtvNoteInputMethod;
      enteredNoteCount: number;
    }
  // Mirrors BackingTrackBpmSuggestion rather than flattening it: that union exists so "a bpm without a source — or a source without a bpm" is unrepresentable, and three loose fields (a boolean plus two nullables the producer always moves together) would hand those impossible states straight back. The wire stays flat — toTtvEventPostHogProperties narrows and emits bpm_detection_succeeded / detected_bpm / bpm_source unchanged.
  | ({
      event: "ttv_backing_track_added";
      durationSeconds: number;
    } & (
      | {
          bpmDetection: "succeeded";
          detectedBpm: number;
          bpmSource: BackingTrackBpmSuggestionSource;
        }
      | { bpmDetection: "failed" }
    ))
  // ONE per user intent, not one per backend run. runKind / trigger / takeKind are deliberately gone: they described an individual leg, and an intent can span several legs of different kinds — take_kind in particular was a constant "lead" because the fan-out legs were suppressed one by one. intentSource says what the user did; the stability trail still carries every leg.
  | ({
      event: "ttv_generate_requested";
      intentSource: TtvGenerationIntentSource;
      resingScope: TtvResingScope | null;
      resingNoteCount: number | null;
      // Distinct singers this intent renders — three for a tempo change that re-times a three-singer roster. The roster size rides along as rosterSingerCount, so neither name has to mean both.
      runSingerCount: number;
    } & TtvGenerationSnapshot)
  | {
      event: "ttv_generate_succeeded";
      intentSource: TtvGenerationIntentSource;
      durationMs: number;
      // The rendered take's own length. Distinct key from the playback denominator above.
      renderedAudioSeconds: number | null;
      runSingerCount: number;
      // How many backend runs the intent actually took. Reported so the cost of an intent is still visible without every leg inflating the funnel.
      legCount: number;
      // The voices this generation actually synthesized, once each (see LiveIntent.runVoiceIds). Unlike rosterVoiceIds on the request, it leaves out roster voices the generation never sang. Carried on the terminal because a gesture's later legs are dispatched after the request event has gone out.
      runVoiceIds: string[];
    }
  | {
      event: "ttv_generate_failed";
      intentSource: TtvGenerationIntentSource;
      durationMs: number;
      errorType: TtvGenerationErrorType;
      runSingerCount: number;
      legCount: number;
      runVoiceIds: string[];
    }
  // The third terminal. An intent whose every leg was aborted or superseded produced no audio and failed at nothing — a newer request replaced it, the user switched project, the run was pre-empted. Counting it as a failure would libel the backend; emitting nothing would leave a requested event with no terminal and break every funnel built on the pair.
  | {
      event: "ttv_generate_cancelled";
      intentSource: TtvGenerationIntentSource;
      durationMs: number;
      runSingerCount: number;
      legCount: number;
    }
  // Growing the unison stack is one gesture that fans out into one whole-part render per new take. Its own event rather than N generations: the funnel would otherwise multiply by the unison count, and before this the whole gesture — and every failure in it — reached PostHog as nothing but a control change.
  | {
      event: "ttv_unison_stack_built";
      previousTakeCount: number;
      requestedTakeCount: number;
      renderedTakeCount: number;
      durationMs: number;
      // "partial" when some new takes landed and others did not. The per-take failure classification stays in the stability trail and in the logged exception — the counts plus this outcome are what the product funnel needs, and a field that could only ever be null is worse than no field. Named for the stack rather than taking the bare "outcome": the stability trail already owns that key with a different vocabulary (applied / superseded / aborted), and one PostHog property name cannot mean two things.
      stackOutcome: TtvGenerationOutcome;
    }
  // A render that reproduces existing audio — a recipe replay, a rigid move, a silence splice — is not a product generation and stays out of the funnel. Its FAILURES are still real: a backend 500 on a note drag, and its automatic retry, used to be invisible to product analytics entirely.
  | {
      event: "ttv_audio_render_failed";
      runKind: AiBackendRun["kind"];
      errorType: TtvGenerationErrorType;
      durationMs: number;
    }
  | {
      event: "ttv_playback_session";
      startPositionSeconds: number;
      stopPositionSeconds: number;
      listenedSeconds: number;
      // What the transport would actually play end to end (the later of vocal, backing clip and score), read when the listen closes. Deliberately NOT the same key as ttv_generate_succeeded's rendered-take length: one wire key, one meaning.
      playableDurationSeconds: number | null;
      // Null only for an empty song. Can exceed 1 when the user seeks back and replays part of the take.
      completionRatio: number | null;
      reachedEnd: boolean;
      seekCount: number;
      hadBackingTrack: boolean;
      isFirstListenOfThisRender: boolean;
    }
  // The domain shape keeps one before/after pair — a caller should not have to know which PostHog property its value lands on. toTtvEventPostHogProperties splits it by type on the way out; see TtvControlValueType.
  | {
      event: "ttv_control_changed";
      controlName: TtvControlName;
      previousValue: TtvControlValue;
      newValue: TtvControlValue;
      isFirstChangeThisSession: boolean;
    }
  | {
      event: "ttv_preset_applied";
      presetType: "harmony";
      presetName: string;
      previousPreset: string | null;
      layerCount: number;
      launchedFrom: "quick_row" | "gallery";
    }
  // Effects presets share the event name but not the payload: they build no singer layers and are applied from one place, so the harmony-only fields are absent rather than filled with placeholders.
  | {
      event: "ttv_preset_applied";
      presetType: "effects";
      presetName: string;
      previousPreset: string | null;
    }
  | {
      event: "ttv_note_edited";
      editType: TtvNoteEditType;
      // The notes this one edit touched, for the kinds that carry a count. Null for the edits whose size is not a number (a lyric commit, a move, a pitch stroke).
      editedNoteCount: number | null;
    }
  | {
      // One phoneme boundary drag in the lane, with what moved and how far. The list of these, by voice, is the pronunciation bug tracker: the sounds people keep fixing by hand are the dictionary and retraining priorities.
      event: "ttv_phoneme_timing_dragged";
      // Null for a label outside the model's phoneme inventory, which is free text and never leaves the browser. The code and its class are always present together, and the code is normalized (see toTtvDraggedPhoneme) so casing never splits a count.
      phoneme: TtvDraggedPhoneme | null;
      // The note's lyric, only when it is a pronunciation-dictionary entry (a fixed vocabulary), so the tracker can name the words people fix without shipping free-text lyrics, names or pasted lines. Null otherwise.
      word: string | null;
      // Null when the note's singer is no longer on the roster, rather than an empty id that reads as a real voice.
      voiceId: string | null;
      // Signed, at the project tempo: negative is earlier.
      deltaMs: number;
      // Whether the handle dragged was a note's own start line (its first phoneme moved) rather than a boundary inside a word. It says which handle, not that the onset left the note's span.
      isNoteStartHandle: boolean;
    }
  | {
      event: "ttv_undo";
      // The kind of the edit being undone, straight off HistoryChange.kind. Typed as that union rather than string, like every other action-kind field on this event union (editType, runKind, errorType).
      undoneActionType: HistoryChangeKind | "unknown";
      undoStackDepth: number;
      // How many versions this one gesture travelled. 1 for a Ctrl+Z; the distance for a version-menu jump, which runs that many restores through the same path and used to report that many events.
      hopCount: number;
    }
  | {
      event: "ttv_redo";
      redoneActionType: HistoryChangeKind | "unknown";
      redoStackDepth: number;
      hopCount: number;
    };

export type RecordedTtvAnalyticsEvent = TtvAnalyticsEvent & {
  recordedAt: number;
  schemaVersion: typeof TTV_ANALYTICS_EVENT_SCHEMA_VERSION;
};

export type TtvPlaybackOperation =
  | {
      kind: "start";
      startedAtContextSeconds: number;
      startPositionSeconds: number;
      hadBackingTrack: boolean;
      isVocalAudible: boolean;
      renderKey: string | null;
      readContextSeconds: () => number;
      readPositionSeconds: () => number;
      readPlayableEndSeconds: () => number | null;
    }
  // Any position change on a live run that keeps sounding. The run never goes silent, so the listen simply continues.
  | { kind: "seek" }
  // A render landed mid-listen, or the mix changed under it (a mute, an unmute, a fader reaching or leaving zero). The listen continues on the same audible output, but everything identifying WHAT is playing — and whether it can be heard at all — has to follow it.
  | {
      kind: "renderSwapped";
      renderKey: string | null;
      isVocalAudible: boolean;
    }
  // The output master changed whether the current take can be heard without replacing its audio or render identity.
  | { kind: "vocalAudibilityChanged"; isVocalAudible: boolean }
  // Playback stopped. Ends the listen unless the ruler is holding it open across a scrub.
  | { kind: "silenced" }
  // Playback stopped to wait for audio that has not rendered yet. The same listen resumes where it left off once the phrase arrives.
  | { kind: "buffered" }
  // Everything that owns the sound is going away — a project switch, the editor unmounting. Ends the listen now, whatever is holding it, so it is always reported against the project and the output it belonged to.
  | { kind: "teardown" }
  // The ruler took the transport. Its pause is a gap inside this listen and its release is the listen resuming somewhere else. Idempotent in both directions: a hold while nothing is playing, or a release with no hold, does nothing at all.
  | { kind: "holdForScrub" }
  | { kind: "releaseScrub" };
